import { useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_PAGE_PARAM = 'page'

type UsePaginationOptions = {
    pageSize?: number
    paramName?: string
    resetDeps?: unknown[]
}

const parsePageFromSearchParams = (searchParams: URLSearchParams, paramName: string): number => {
    const value = searchParams.get(paramName)
    if (!value) return 1
    const page = parseInt(value, 10)
    return Number.isFinite(page) && page >= 1 ? page : 1
}

export const usePagination = (options?: UsePaginationOptions) => {
    const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE
    const paramName = options?.paramName ?? DEFAULT_PAGE_PARAM
    const resetDeps = options?.resetDeps

    const [searchParams, setSearchParams] = useSearchParams()
    const currentPage = parsePageFromSearchParams(searchParams, paramName)
    const prevResetDeps = useRef<unknown[]>([])
    const isFirstRun = useRef(true)

    useEffect(() => {
        if (!resetDeps) return

        if (isFirstRun.current) {
            isFirstRun.current = false
            prevResetDeps.current = [...resetDeps]
            return
        }

        const prev = prevResetDeps.current
        const hasMeaningfulChange = resetDeps.some(
            (dep, i) => prev[i] !== undefined && dep !== undefined && prev[i] !== dep
        )
        prevResetDeps.current = [...resetDeps]

        if (hasMeaningfulChange) {
            setSearchParams(
                prevParams => {
                    const next = new URLSearchParams(prevParams)
                    next.set(paramName, '1')
                    return next
                },
                { replace: true }
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resetDeps is dynamic
    }, [paramName, setSearchParams, ...(resetDeps ?? [])])

    const handlePageChange = useCallback(
        (page: number) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    next.set(paramName, String(page))
                    return next
                },
                { replace: true }
            )
        },
        [paramName, setSearchParams]
    )

    const setCurrentPage = handlePageChange

    return {
        currentPage,
        pageSize,
        handlePageChange,
        setCurrentPage,
    }
}
